import React, { useState, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Alert,
  AlertTitle,
  Paper,
  Stack,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  TextField,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowForward as ArrowForwardIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const PREVIEW_ROW_LIMIT = 100;       // por secao agrupada
const FULL_TABLE_ROW_LIMIT = 200;    // tabela completa

const STEPS = ['Selecionar planilha', 'Preview e validacao', 'Resultado'];

const ACOES_LABEL = {
  criar: 'Criar',
  atualizar: 'Atualizar',
  manter: 'Manter',
  trocar: 'Trocar',
  corrigir_ativo: 'Corrigir ativo',
};

const CHIP_DEFS = [
  { key: 'total_linhas', label: 'Linhas', color: 'default', tip: 'Total de linhas validas processadas (linhas em branco do Excel sao ignoradas).' },
  { key: 'empresa_criada', label: 'Empresa nova', color: 'success', tip: 'Empresas que serao INSERIDAS na tabela Empresa porque o CodigoEmpresa nao existe no banco.' },
  { key: 'empresa_atualizada', label: 'Empresa atualizada', color: 'info', tip: 'Empresas existentes com algum campo divergente da planilha (NomeFantasia, TipoAtendimento). Cada campo divergente sera atualizado.' },
  { key: 'empresa_ja_ok', label: 'Empresa ja OK', color: 'default', tip: 'Empresas existentes sem nenhum campo divergente.' },
  { key: 'hist_criado', label: 'Hist. novo', color: 'success', tip: 'Linhas em HistoricoResponsaveis que serao CRIADAS (empresa nova ou empresa sem historico ativo).' },
  { key: 'hist_trocado', label: 'Hist. trocado', color: 'warning', tip: 'TROCAS de comercial: o historico ativo atual recebe DataFim = (data_atualizacao - 1) e um novo registro e aberto a partir da data da planilha.' },
  { key: 'hist_corrigido', label: 'Hist. corrigido', color: 'warning', tip: 'CORRECAO no proprio registro ativo (UPDATE em IDResponsavel) quando a data de atualizacao da planilha e <= DataInicio do ativo (mesmo dia). Nenhum registro novo e criado.' },
  { key: 'hist_ja_ok', label: 'Hist. ja OK', color: 'default', tip: 'Empresas com historico ativo apontando para o mesmo Responsavel da planilha. Nenhuma alteracao.' },
  { key: 'responsavel_criado', label: 'Responsavel novo', color: 'success', tip: 'Responsaveis que serao criados na tabela Responsavel porque o nome da planilha nao existe no banco (match por nome normalizado).' },
  { key: 'erros', label: 'Erros', color: 'error', tip: 'Linhas com erro de validacao. Se > 0, a gravacao fica BLOQUEADA.' },
];

const fmtData = (v) => {
  if (!v) return '-';
  const s = String(v);
  // Backend devolve ISO yyyy-mm-dd. Converte para dd/mm/aaaa (pt-BR).
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  // Fallback: tenta Date com locale pt-BR
  const dt = new Date(s);
  if (!isNaN(dt.getTime())) return dt.toLocaleDateString('pt-BR');
  return s;
};

const StatChip = ({ chipDef, value }) => (
  <Tooltip title={chipDef.tip} arrow placement="top">
    <Chip
      label={`${chipDef.label}: ${value || 0}`}
      color={value > 0 ? chipDef.color : 'default'}
      variant="outlined"
      size="small"
    />
  </Tooltip>
);

const SeccaoMudanca = ({ titulo, count, color, defaultExpanded, children }) => {
  if (!count) return null;
  return (
    <Accordion defaultExpanded={defaultExpanded} sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip label={count} color={color} size="small" />
          <Typography variant="subtitle2">{titulo}</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
};

const TabelaMudancas = ({ rows, colunas, limite = PREVIEW_ROW_LIMIT }) => (
  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          {colunas.map((c) => (
            <TableCell key={c.key} sx={{ fontWeight: 'bold' }}>{c.label}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.slice(0, limite).map((r, i) => (
          <TableRow key={i}>
            {colunas.map((c) => (
              <TableCell key={c.key}>{c.render ? c.render(r) : r[c.key]}</TableCell>
            ))}
          </TableRow>
        ))}
        {rows.length > limite && (
          <TableRow>
            <TableCell colSpan={colunas.length} sx={{ fontStyle: 'italic', textAlign: 'center', bgcolor: 'warning.lighter' }}>
              Exibindo {limite} de {rows.length}. Para validar todos os registros, baixe o relatorio (.xlsx) no botao no rodape do dialog.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

const SetaMudanca = ({ antes, depois, mesmaCor }) => (
  <Stack direction="row" alignItems="center" spacing={0.5}>
    <Typography variant="body2" color="text.secondary" sx={{ textDecoration: mesmaCor ? 'none' : 'line-through' }}>
      {antes ?? '-'}
    </Typography>
    <ArrowForwardIcon fontSize="inherit" color="action" />
    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{depois ?? '-'}</Typography>
  </Stack>
);

const CargaEmpresasDialog = ({ open, onClose, onSuccess }) => {
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [erroApi, setErroApi] = useState('');
  const [filtroLinha, setFiltroLinha] = useState('');
  const inputRef = useRef(null);

  const reset = () => {
    setStep(0);
    setFile(null);
    setPreview(null);
    setResultado(null);
    setErroApi('');
    setFiltroLinha('');
    setLoading(false);
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const enviar = async (execute) => {
    if (!file) return;
    setLoading(true);
    setErroApi('');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post(
        `/admin/empresas/carga?execute=${execute ? 'true' : 'false'}`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const data = res.data?.data || res.data;
      if (execute) {
        setResultado(data);
        setStep(2);
        onSuccess?.();
      } else {
        setPreview(data);
        setStep(1);
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.message;
      setErroApi(msg);
    } finally {
      setLoading(false);
    }
  };

  const baixarRelatorio = async () => {
    if (!file) return;
    setLoading(true);
    setErroApi('');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post(
        '/admin/empresas/carga/relatorio',
        form,
        { headers: { 'Content-Type': 'multipart/form-data' }, responseType: 'blob' }
      );
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
      a.download = `relatorio_carga_empresas_${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e.response?.data?.message || e.message;
      setErroApi(`Falha ao baixar relatorio: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const grupos = useMemo(() => {
    const empty = { criar_emp: [], atualizar_emp: [], hist_criar: [], hist_trocar: [], hist_corrigir: [], hist_ok: [] };
    if (!preview?.decisoes) return empty;
    const out = { ...empty };
    preview.decisoes.forEach((d) => {
      if (d.erros?.length) return;
      if (d.acao_empresa === 'criar') out.criar_emp.push(d);
      if (d.acao_empresa === 'atualizar') out.atualizar_emp.push(d);
      if (d.acao_historico === 'criar' && d.acao_empresa !== 'criar') out.hist_criar.push(d);
      if (d.acao_historico === 'trocar') out.hist_trocar.push(d);
      if (d.acao_historico === 'corrigir_ativo') out.hist_corrigir.push(d);
      if (d.acao_empresa === 'manter' && d.acao_historico === 'manter') out.hist_ok.push(d);
    });
    return out;
  }, [preview]);

  const decisoesFiltradas = useMemo(() => {
    if (!preview) return [];
    if (!filtroLinha) return preview.decisoes;
    const f = filtroLinha.toLowerCase();
    return preview.decisoes.filter((d) =>
      String(d.codigo || '').includes(f) ||
      (d.nome || '').toLowerCase().includes(f) ||
      (d.responsavel_nome || '').toLowerCase().includes(f) ||
      String(d.linha || '').includes(f)
    );
  }, [preview, filtroLinha]);

  const renderStats = (stats, titulo) => {
    if (!stats) return null;
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>{titulo} — passe o mouse em cada metrica para entender</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {CHIP_DEFS.map((cd) => (
            <StatChip key={cd.key} chipDef={cd} value={stats[cd.key]} />
          ))}
        </Stack>
      </Paper>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Carga em massa de empresas
        <IconButton onClick={handleClose} disabled={loading}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map((s) => (
            <Step key={s}><StepLabel>{s}</StepLabel></Step>
          ))}
        </Stepper>

        {erroApi && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErroApi('')}>{erroApi}</Alert>
        )}

        {step === 0 && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Envie a planilha do Agendor (.xlsx) com as colunas:
              <strong> Codigo da Empresa, Razao Social / Nome Fantasia, Tipo de Atendimento,
              Data de Cadastro, Usuario Responsavel, Data de Atualizacao do Comercial</strong>.
              O sistema valida tudo antes de gravar.
            </Alert>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls"
              hidden
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            <Paper
              variant="outlined"
              sx={{
                p: 4, textAlign: 'center', cursor: 'pointer',
                borderStyle: 'dashed', borderWidth: 2,
                bgcolor: file ? 'action.hover' : 'transparent',
              }}
              onClick={() => inputRef.current?.click()}
            >
              <UploadIcon sx={{ fontSize: 48, color: 'action.active', mb: 1 }} />
              <Typography variant="body1">
                {file ? file.name : 'Clique para selecionar o arquivo .xlsx'}
              </Typography>
              {file && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {(file.size / 1024).toFixed(1)} KB
                </Typography>
              )}
            </Paper>
          </Box>
        )}

        {step === 1 && preview && (
          <Box>
            {preview.erros?.length > 0 ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>{preview.erros.length} erro(s) encontrados</AlertTitle>
                Corrija a planilha e envie novamente. A gravacao esta bloqueada.
              </Alert>
            ) : (
              <Alert severity="success" sx={{ mb: 2 }}>
                <AlertTitle>Validacao OK</AlertTitle>
                Planilha sem erros. Revise as mudancas abaixo e confirme para gravar.
              </Alert>
            )}

            {renderStats(preview.stats, 'Resumo')}

            {preview.novos_responsaveis?.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>{preview.novos_responsaveis.length} responsavel(is) sera(o) criado(s) na tabela Responsavel</AlertTitle>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  {preview.novos_responsaveis.map((r, i) => (
                    <Chip key={i} label={r.nome} size="small" color="warning" />
                  ))}
                </Stack>
              </Alert>
            )}

            {preview.warnings?.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom color="warning.main">
                  Avisos ({preview.warnings.length})
                </Typography>
                <Box sx={{ maxHeight: 150, overflow: 'auto' }}>
                  {preview.warnings.slice(0, 50).map((w, i) => (
                    <Typography key={i} variant="body2">
                      Linha {w.linha}: {w.mensagem}
                    </Typography>
                  ))}
                  {preview.warnings.length > 50 && (
                    <Typography variant="caption" color="text.secondary">
                      ...e mais {preview.warnings.length - 50}
                    </Typography>
                  )}
                </Box>
              </Paper>
            )}

            {preview.erros?.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom color="error">
                  Erros ({preview.erros.length})
                </Typography>
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {preview.erros.slice(0, 100).map((e, i) => (
                    <Typography key={i} variant="body2" color="error">
                      {e.tipo === 'cabecalho'
                        ? e.mensagem
                        : `Linha ${e.linha} (cod ${e.codigo ?? '?'}): ${(e.erros || []).join('; ')}`}
                    </Typography>
                  ))}
                  {preview.erros.length > 100 && (
                    <Typography variant="caption" color="text.secondary">
                      ...e mais {preview.erros.length - 100}
                    </Typography>
                  )}
                </Box>
              </Paper>
            )}

            <Divider sx={{ my: 2 }}>
              <Chip label="O QUE VAI MUDAR NO BANCO" size="small" />
            </Divider>

            <SeccaoMudanca
              titulo="Empresas novas (INSERT em Empresa)"
              count={grupos.criar_emp.length}
              color="success"
              defaultExpanded
            >
              <TabelaMudancas
                rows={grupos.criar_emp}
                colunas={[
                  { key: 'linha', label: 'Linha' },
                  { key: 'codigo', label: 'Codigo' },
                  { key: 'nome', label: 'Nome Fantasia' },
                  { key: 'tipo_atendimento', label: 'Tipo Atend.' },
                  { key: 'responsavel_nome', label: 'Responsavel' },
                  { key: 'data_atualizacao', label: 'Data inicio do historico', render: (r) => fmtData(r.data_atualizacao) },
                ]}
              />
            </SeccaoMudanca>

            <SeccaoMudanca
              titulo="Empresas atualizadas (campos divergentes da planilha)"
              count={grupos.atualizar_emp.length}
              color="info"
              defaultExpanded
            >
              <TabelaMudancas
                rows={grupos.atualizar_emp}
                colunas={[
                  { key: 'linha', label: 'Linha' },
                  { key: 'codigo', label: 'Codigo' },
                  { key: 'nome_existente', label: 'Empresa atual' },
                  {
                    key: 'campos',
                    label: 'Campos alterados (antes -> depois)',
                    render: (r) => (
                      <Stack spacing={0.5}>
                        {(r.campos_alterados || []).map((c, i) => (
                          <Stack key={i} direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', minWidth: 110 }}>
                              {c.campo}:
                            </Typography>
                            <SetaMudanca antes={c.antes || '(vazio)'} depois={c.depois} />
                          </Stack>
                        ))}
                      </Stack>
                    ),
                  },
                ]}
              />
            </SeccaoMudanca>

            <SeccaoMudanca
              titulo="Historicos novos (INSERT em HistoricoResponsaveis)"
              count={grupos.hist_criar.length}
              color="success"
              defaultExpanded
            >
              <TabelaMudancas
                rows={grupos.hist_criar}
                colunas={[
                  { key: 'linha', label: 'Linha' },
                  { key: 'codigo', label: 'Codigo' },
                  { key: 'nome_existente', label: 'Empresa', render: (r) => r.nome_existente || r.nome },
                  { key: 'responsavel_nome', label: 'Responsavel' },
                  { key: 'data_atualizacao', label: 'DataInicio', render: (r) => fmtData(r.data_atualizacao) },
                ]}
              />
            </SeccaoMudanca>

            <SeccaoMudanca
              titulo="Trocas de comercial (fecha ativo + abre novo registro)"
              count={grupos.hist_trocar.length}
              color="warning"
              defaultExpanded
            >
              <TabelaMudancas
                rows={grupos.hist_trocar}
                colunas={[
                  { key: 'linha', label: 'Linha' },
                  { key: 'codigo', label: 'Codigo' },
                  { key: 'nome_existente', label: 'Empresa', render: (r) => r.nome_existente || r.nome },
                  {
                    key: 'troca',
                    label: 'Comercial (antes -> depois)',
                    render: (r) => (
                      <SetaMudanca
                        antes={r.responsavel_anterior_nome || `id=${r.responsavel_anterior_id}`}
                        depois={r.responsavel_nome}
                      />
                    ),
                  },
                  {
                    key: 'datas',
                    label: 'Vigencia ativo: inicio -> novo fim',
                    render: (r) => (
                      <SetaMudanca
                        antes={`${fmtData(r.data_inicio_ativo_anterior)} - aberto`}
                        depois={`${fmtData(r.data_inicio_ativo_anterior)} - ${fmtData(r.data_fim_calculada)}`}
                      />
                    ),
                  },
                  {
                    key: 'novo',
                    label: 'Novo registro',
                    render: (r) => (
                      <Typography variant="body2">
                        {fmtData(r.data_atualizacao)} - aberto
                      </Typography>
                    ),
                  },
                ]}
              />
            </SeccaoMudanca>

            <SeccaoMudanca
              titulo="Correcoes no historico ativo (UPDATE em IDResponsavel, mantem DataInicio)"
              count={grupos.hist_corrigir.length}
              color="warning"
              defaultExpanded
            >
              <TabelaMudancas
                rows={grupos.hist_corrigir}
                colunas={[
                  { key: 'linha', label: 'Linha' },
                  { key: 'codigo', label: 'Codigo' },
                  { key: 'nome_existente', label: 'Empresa', render: (r) => r.nome_existente || r.nome },
                  {
                    key: 'troca',
                    label: 'Comercial (antes -> depois)',
                    render: (r) => (
                      <SetaMudanca
                        antes={r.responsavel_anterior_nome || `id=${r.responsavel_anterior_id}`}
                        depois={r.responsavel_nome}
                      />
                    ),
                  },
                  {
                    key: 'datainicio',
                    label: 'DataInicio mantida',
                    render: (r) => fmtData(r.data_inicio_ativo_anterior),
                  },
                ]}
              />
            </SeccaoMudanca>

            <SeccaoMudanca
              titulo="Sem alteracoes (empresa e comercial ja conferem)"
              count={grupos.hist_ok.length}
              color="default"
              defaultExpanded={false}
            >
              <TabelaMudancas
                rows={grupos.hist_ok}
                colunas={[
                  { key: 'linha', label: 'Linha' },
                  { key: 'codigo', label: 'Codigo' },
                  { key: 'nome_existente', label: 'Empresa', render: (r) => r.nome_existente || r.nome },
                  { key: 'responsavel_nome', label: 'Comercial atual' },
                ]}
              />
            </SeccaoMudanca>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2 }}>
              <Typography variant="subtitle2">
                Tabela completa por linha ({decisoesFiltradas.length}/{preview.decisoes.length})
              </Typography>
              <TextField
                size="small"
                placeholder="Filtrar por codigo, nome, responsavel ou linha"
                value={filtroLinha}
                onChange={(e) => setFiltroLinha(e.target.value)}
                sx={{ flexGrow: 1, maxWidth: 400 }}
              />
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Linha</TableCell>
                    <TableCell>Codigo</TableCell>
                    <TableCell>Nome</TableCell>
                    <TableCell>Responsavel</TableCell>
                    <TableCell>Acao Empresa</TableCell>
                    <TableCell>Acao Historico</TableCell>
                    <TableCell>Erros</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {decisoesFiltradas.slice(0, FULL_TABLE_ROW_LIMIT).map((d, i) => (
                    <TableRow key={i}>
                      <TableCell>{d.linha}</TableCell>
                      <TableCell>{d.codigo}</TableCell>
                      <TableCell>{d.nome_existente || d.nome}</TableCell>
                      <TableCell>
                        {d.responsavel_nome}
                        {d.responsavel_novo && <Chip label="novo" size="small" color="warning" sx={{ ml: 1 }} />}
                      </TableCell>
                      <TableCell>{ACOES_LABEL[d.acao_empresa] || d.acao_empresa || '-'}</TableCell>
                      <TableCell>{ACOES_LABEL[d.acao_historico] || d.acao_historico || '-'}</TableCell>
                      <TableCell>
                        {d.erros?.length > 0 && (
                          <Tooltip title={d.erros.join('; ')}>
                            <Chip label={`${d.erros.length} erro(s)`} size="small" color="error" />
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {decisoesFiltradas.length > FULL_TABLE_ROW_LIMIT && (
              <Alert severity="info" sx={{ mt: 1 }} icon={<DownloadIcon />}>
                Exibindo {FULL_TABLE_ROW_LIMIT} de {decisoesFiltradas.length} linhas. Use o filtro acima ou
                <strong> baixe o relatorio completo (.xlsx)</strong> no botao no rodape para validar todos os registros offline.
              </Alert>
            )}
          </Box>
        )}

        {step === 2 && resultado && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              <AlertTitle>Carga concluida</AlertTitle>
              {resultado.stats.empresa_criada} empresa(s) nova(s),
              {' '}{resultado.stats.hist_trocado} troca(s) de comercial,
              {' '}{resultado.stats.hist_corrigido} correcao(oes),
              {' '}{resultado.stats.responsavel_criado} responsavel(is) novo(s).
              {' '}<strong>{resultado.resultados_bi_atualizados}</strong> registro(s) do Resultados_BI atualizado(s).
            </Alert>
            {renderStats(resultado.stats, 'Resumo final')}
            <Alert severity="info" sx={{ mt: 2 }}>
              Se quiser arquivar o detalhe do que foi gravado, baixe o relatorio em .xlsx no botao
              do rodape. Nada e salvo no servidor.
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {step === 0 && (
          <>
            <Button onClick={handleClose} disabled={loading}>Cancelar</Button>
            <Button
              variant="contained"
              onClick={() => enviar(false)}
              disabled={!file || loading}
              startIcon={loading ? <CircularProgress size={18} /> : <RefreshIcon />}
            >
              Validar planilha
            </Button>
          </>
        )}
        {step === 1 && (
          <>
            <Button onClick={() => { setStep(0); setPreview(null); }} disabled={loading}>Voltar</Button>
            <Button
              variant="outlined"
              onClick={baixarRelatorio}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} /> : <DownloadIcon />}
            >
              Baixar relatorio (.xlsx)
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={() => enviar(true)}
              disabled={loading || (preview?.erros?.length > 0)}
              startIcon={loading ? <CircularProgress size={18} /> : null}
            >
              Confirmar e gravar
            </Button>
          </>
        )}
        {step === 2 && (
          <>
            <Button
              variant="outlined"
              onClick={baixarRelatorio}
              disabled={loading || !file}
              startIcon={loading ? <CircularProgress size={18} /> : <DownloadIcon />}
            >
              Baixar relatorio (.xlsx)
            </Button>
            <Button variant="contained" onClick={handleClose}>Fechar</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CargaEmpresasDialog;
